import { useState, useEffect, useContext } from "react";
import { post, C, LOGO_BASE } from "./utils";
import { useFileViewer, FilePill, ImageThumb, thumbUrl, splitChatbot } from "./FileViewer";
import { ThemeCtx, Icon } from "./AppShell";

const isImage = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName= (p="") => p.split("/").pop();
const stripHtml=(h="")=> h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";

const DT_CFG = {
  95:{ color:"#2563EB", bg:"#EFF6FF", label:"News"      },
  92:{ color:"#7C3AED", bg:"#F5F3FF", label:"Reports"   },
  94:{ color:"#059669", bg:"#ECFDF5", label:"Events"    },
  96:{ color:"#0891B2", bg:"#ECFEFF", label:"Trial"     },
  97:{ color:"#DB2777", bg:"#FDF2F8", label:"Profile"   },
  88:{ color:"#B45309", bg:"#FEF3C7", label:"Posters"   },
  87:{ color:"#065F46", bg:"#DCFCE7", label:"Exhibits"  },
  99:{ color:"#EA580C", bg:"#FFF7ED", label:"NewsFlash" },
};
const dtc = id => DT_CFG[Number(id)] || { color:"#6B7280", bg:"#F9FAFB", label:"Doc" };

// Parse "2025-09-01" ISO date
const fmtISO = s => {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-GB",{ day:"2-digit", month:"short", year:"numeric" });
};

// Format DISPLAY_TIME range "2025-09-01 to 2025-09-30" → "01 Sep 2025 – 30 Sep 2025"
const fmtRange = s => {
  if (!s) return "";
  const m = s.match(/(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})/);
  if (m) return `${fmtISO(m[1])} – ${fmtISO(m[2])}`;
  return s;
};

// Extract year group from STARTDATE
const yearOf = s => s ? s.slice(0,4) : "—";

// ─── Loaders ──────────────────────────────────────────────────────────────────
// Call 1: list — no documentid
function useListLoader({ user, subproject, stateid=2 }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!subproject || !user) return;
    setLoading(true);
    post("newsletter", {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid,
    }, user.AUTH_CODE)
      .then(r=>{ if(r.STATUS==="1") setItems(Array.isArray(r.DATA)?r.DATA:[]); })
      .catch(()=>{}).finally(()=>setLoading(false));
  }, [subproject?.SUBPROJECTID, stateid]);
  return { items, loading };
}

// Call 2: detail — with documentid; extracts LOGO + NOTES + RELATIVE from root
function useDetailLoader({ user, subproject, documentId }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!subproject || !user || !documentId) return;
    setLoading(true); setDetail(null); // always clear before fetch
    post("newsletter", {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid:      2,
      documentid:   documentId,
    }, user.AUTH_CODE)
      .then(r=>{
        if(r.STATUS==="1"){
          // Root-level fields: LOGO, NOTES, RELATIVE
          // DATA array has the doc itself
          const doc = Array.isArray(r.DATA) ? r.DATA[0] : (r.DATA||{});
          setDetail({
            ...doc,
            LOGO:     r.LOGO     || doc.LOGO     || null,
            NOTES:    r.NOTES    || doc.NOTES    || "",
            RELATIVE: r.RELATIVE || doc.RELATIVE || [],
          });
        }
      })
      .catch(()=>{}).finally(()=>setLoading(false));
  }, [documentId, subproject?.SUBPROJECTID]);
  return { detail, loading };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ color }) {
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",
      padding:"3rem",gap:"8px",color:"#9CA3AF",fontSize:"13px" }}>
      <div style={{ width:"14px",height:"14px",border:`2px solid #E5E7EB`,
        borderTopColor:color,borderRadius:"50%",animation:"_nf 0.8s linear infinite" }}/>
      <style>{"@keyframes _nf{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ─── Left: single list item ───────────────────────────────────────────────────
function ListItem({ doc, active, onClick, tPrimary }) {
  const range = fmtRange(doc.DISPLAY_TIME);
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ padding:"9px 14px", cursor:"pointer",
        borderLeft:`3px solid ${active?tPrimary:"transparent"}`,
        background: active?`${tPrimary}0E`:hov?"#F8FAFC":"transparent",
        borderBottom:"1px solid #F1F5F9", transition:"all 0.1s" }}>
      <div style={{ display:"flex",alignItems:"center",gap:"8px" }}>
        {/* Month icon */}
        <div style={{ width:"36px",height:"36px",borderRadius:"8px",flexShrink:0,
          background: active?`${tPrimary}18`:"#F1F5F9",
          border:`1px solid ${active?tPrimary+"33":"#E5E7EB"}`,
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center" }}>
          {(()=>{
            const d = new Date((doc.STARTDATE||"")+"T00:00:00");
            const mon = isNaN(d)?null:d.toLocaleDateString("en-GB",{month:"short"});
            const yr  = isNaN(d)?null:d.getFullYear()%100;
            return mon ? <>
              <span style={{ fontSize:"8px",fontWeight:"800",
                color:active?tPrimary:"#6B7280",lineHeight:1,letterSpacing:"0.3px" }}>
                {mon.toUpperCase()}
              </span>
              <span style={{ fontSize:"10px",fontWeight:"900",
                color:active?tPrimary:"#374151",lineHeight:1 }}>'{yr}</span>
            </> : <Icon name="newsletter" size={14} color={active?tPrimary:"#9CA3AF"}/>;
          })()}
        </div>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ fontSize:"12px",fontWeight:active?"700":"600",
            color:active?tPrimary:"#111827",
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.3 }}>
            {doc.TITLE||"Untitled"}
          </div>
          {range && <div style={{ fontSize:"10px",color:"#9CA3AF",marginTop:"2px",
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
            {range}
          </div>}
        </div>
        {active && <div style={{ width:"6px",height:"6px",borderRadius:"50%",
          background:tPrimary,flexShrink:0 }}/>}
      </div>
    </div>
  );
}

// ─── Related doc row (list view) ─────────────────────────────────────────────
function RelatedCard({ doc, tPrimary, subId }) {
  const [hov, setHov] = useState(false);
  const cfg   = dtc(doc.DOCTYPEID);
  const logo  = doc.COMPANY_LOGO ? LOGO_BASE+doc.COMPANY_LOGO : null;
  const notes = stripHtml(doc.MESSAGES||doc.NOTES||"");

  const openDoc = () => {
    if(!doc.DOCUMENTID||!doc.DOCTYPEID) return;
    const base = window.location.origin + window.location.pathname;
    window.open(`${base}?fdetail=1&doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`,
      "_blank","noopener,noreferrer");
  };

  return (
    <div onClick={openDoc}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ display:"flex",gap:"12px",padding:"10px 12px",
        borderRadius:"10px",cursor:"pointer",transition:"all 0.12s",
        background:"#fff",
        borderLeft:`3px solid ${hov?cfg.color:"transparent"}`,
        boxShadow:hov?"0 2px 12px rgba(0,0,0,0.08)":"0 1px 3px rgba(0,0,0,0.04)",
        border:`1px solid ${hov?cfg.color:"#E5E7EB"}` }}>

      {/* thumbnail / logo */}
      <div style={{ width:"60px",height:"60px",borderRadius:"8px",flexShrink:0,
        background:logo?"#F8FAFC":`${cfg.color}12`,
        display:"flex",alignItems:"center",justifyContent:"center",
        overflow:"hidden",border:"1px solid #F1F5F9" }}>
        {logo
          ? <img src={logo} alt="" onError={e=>e.target.style.display="none"}
              style={{ width:"100%",height:"100%",objectFit:"contain",padding:"6px" }}/>
          : <span style={{ fontSize:"22px",opacity:0.5 }}>📄</span>
        }
      </div>

      {/* content */}
      <div style={{ flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:"3px" }}>
        {/* title */}
        <div style={{ fontSize:"13px",fontWeight:"700",color:"#111827",lineHeight:"1.4",
          display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden" }}>
          {String(doc.BRANDID)!=="0" && doc.BRANDNAME
            ? <><span style={{color:tPrimary}}>{doc.BRANDNAME}, </span></> : ""}
          {doc.TITLE||doc.SUBJECT||"—"}
        </div>
        {/* company · product · indication — skip if ID=0 */}
        <div style={{ fontSize:"11px",color:"#6B7280",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
          {[
            String(doc.SPONSORID)!=="0" ? doc.SPONSORNAME : null,
            String(doc.BRANDID)!=="0"   ? doc.BRANDNAME   : null,
            String(doc.INDICATIONID)!=="0" ? doc.INDICATIONNAME : null,
          ].filter(Boolean).join(" · ")}
        </div>
        {/* notes preview */}
        {notes && (
          <div style={{ fontSize:"11px",color:"#9CA3AF",lineHeight:"1.5",
            display:"-webkit-box",WebkitLineClamp:2,
            WebkitBoxOrient:"vertical",overflow:"hidden" }}>
            {notes}
          </div>
        )}
        {/* doc type badge */}
        <div style={{ marginTop:"2px" }}>
          <span style={{ fontSize:"10px",fontWeight:"700",color:cfg.color,
            background:cfg.bg,borderRadius:"5px",padding:"1px 7px" }}>
            {doc.DOCTYPENAME||cfg.label}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Newsletter Share Modal ───────────────────────────────────────────────────
function NewsletterShareModal({ doc, user, activeSub, tPrimary, onClose }) {
  const [tab,       setTab]       = useState("users"); // users | email
  const [search,    setSearch]    = useState("");
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [selected,  setSelected]  = useState([]); // userids
  const [emails,    setEmails]    = useState([]);
  const [emailInput,setEmailInput]= useState("");
  const [sending,   setSending]   = useState(false);
  const [result,    setResult]    = useState(null);

  // load users
  useEffect(()=>{
    if(tab!=="users"||users.length>0) return;
    setLoading(true);
    fetch(`https://cip.medsphere.net/api/DM_Users?userid=${user.USERID}&subprojectid=${activeSub?.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{ if(res.STATUS==="1"&&Array.isArray(res.DATA)) setUsers(res.DATA); })
      .catch(()=>{}).finally(()=>setLoading(false));
  },[tab]);

  const toggleUser = (id) =>
    setSelected(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);

  const filtered = users.filter(u=>(u.NAME||"").toLowerCase().includes(search.toLowerCase()));
  const allSel  = filtered.length>0 && filtered.every(u=>selected.includes(u.USERID));
  const partSel = !allSel && filtered.some(u=>selected.includes(u.USERID));
  const toggleAll = () => {
    const ids = filtered.map(u=>u.USERID);
    setSelected(allSel ? selected.filter(id=>!ids.includes(id)) : [...new Set([...selected,...ids])]);
  };

  const addEmail = (raw) => {
    const vals = raw.split(/[,\s]+/).map(e=>e.trim().toLowerCase()).filter(e=>e.includes("@"));
    if(!vals.length) return;
    setEmails(prev=>[...new Set([...prev,...vals])]);
    setEmailInput("");
  };

  const send = async () => {
    if(!selected.length&&!emails.length) return;
    setSending(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("documentid",   doc.DOCUMENTID);
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub?.SUBPROJECTID||"");
      selected.forEach(id=>fd.append("receiver[]",id));
      emails.forEach(e=>fd.append("email[]",e));
      const r   = await fetch("https://cip.medsphere.net/api/share_newsletter",
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1) {
        setResult({ok:true,msg:`Shared successfully with ${selected.length+emails.length} recipient${(selected.length+emails.length)!==1?"s":""}`});
        setSelected([]); setEmails([]);
      } else {
        setResult({ok:false,msg:res.MESSAGE||res.message||"Share failed."});
      }
    } catch { setResult({ok:false,msg:"Network error."}); }
    setSending(false);
  };

  const totalSelected = selected.length + emails.length;

  return (
    <>
      <div onMouseDown={onClose}
        style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:2000}}/>
      <div onMouseDown={e=>e.stopPropagation()}
        style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
          width:"min(500px,92vw)",background:"#fff",borderRadius:12,
          boxShadow:"0 20px 60px rgba(0,0,0,0.2)",zIndex:2001,
          display:"flex",flexDirection:"column",maxHeight:"85vh",overflow:"hidden"}}>

        {/* header */}
        <div style={{padding:"14px 18px",borderBottom:"1px solid #E5E7EB",
          display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:"#111"}}>Share NewsFlash</div>
            <div style={{fontSize:11,color:"#9CA3AF",marginTop:1,
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:340}}>
              {doc.TITLE||""}
            </div>
          </div>
          <button onClick={onClose}
            style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#9CA3AF",padding:0}}>×</button>
        </div>

        {/* tabs */}
        <div style={{display:"flex",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
          {[{id:"users",label:"👥 Users"},{id:"email",label:"📧 Email"}].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{flex:1,padding:"9px",border:"none",background:"none",cursor:"pointer",
                fontSize:13,fontWeight:tab===t.id?700:500,
                color:tab===t.id?tPrimary:"#6B7280",
                borderBottom:`2px solid ${tab===t.id?tPrimary:"transparent"}`,
                transition:"all .12s"}}>
              {t.label}
              {t.id==="users"&&selected.length>0&&(
                <span style={{marginLeft:5,background:tPrimary,color:"#fff",
                  borderRadius:10,padding:"0 6px",fontSize:10,fontWeight:700}}>
                  {selected.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* body */}
        <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>

          {/* users tab */}
          {tab==="users" && (
            <>
              <div style={{padding:"10px 14px",borderBottom:"1px solid #F3F4F6",flexShrink:0,display:"flex",alignItems:"center",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6,flex:1,
                  background:"#F7F9FC",borderRadius:8,padding:"6px 10px",
                  border:"1px solid #E5E7EB"}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/></svg>
                  <input value={search} onChange={e=>setSearch(e.target.value)}
                    placeholder="Search users…"
                    style={{border:"none",outline:"none",fontSize:13,background:"transparent",
                      flex:1,fontFamily:"inherit",color:"#374151"}}/>
                </div>
                {/* Select All */}
                <div onClick={toggleAll} style={{display:"flex",alignItems:"center",gap:5,
                  cursor:"pointer",flexShrink:0,fontSize:12,color:"#6B7280",userSelect:"none"}}>
                  <div style={{width:17,height:17,borderRadius:4,flexShrink:0,
                    border:`2px solid ${allSel||partSel?tPrimary:"#D1D5DB"}`,
                    background:allSel?tPrimary:"#fff",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {allSel&&<svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    {partSel&&!allSel&&<div style={{width:7,height:2,background:tPrimary,borderRadius:1}}/>}
                  </div>
                  All
                </div>
              </div>
              <div style={{flex:1,overflowY:"auto"}}>
                {loading && (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                    padding:"30px",gap:8,color:"#9CA3AF",fontSize:13}}>
                    <div style={{width:14,height:14,border:`2px solid #E5E7EB`,
                      borderTopColor:tPrimary,borderRadius:"50%",
                      animation:"_nf 0.8s linear infinite"}}/>
                    Loading…
                  </div>
                )}
                {!loading && filtered.map(u=>{
                  const sel = selected.includes(u.USERID);
                  const ini = (u.NAME||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                  return (
                    <div key={u.USERID} onClick={()=>toggleUser(u.USERID)}
                      style={{display:"flex",alignItems:"center",gap:10,
                        padding:"9px 14px",cursor:"pointer",
                        background:sel?`${tPrimary}08`:"transparent",
                        borderBottom:"1px solid #F9FAFB",transition:"background .1s"}}
                      onMouseEnter={e=>{if(!sel)e.currentTarget.style.background="#F9FAFB";}}
                      onMouseLeave={e=>{if(!sel)e.currentTarget.style.background="transparent";}}>
                      {/* avatar */}
                      <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,
                        background:`${tPrimary}20`,display:"flex",alignItems:"center",
                        justifyContent:"center",fontSize:12,fontWeight:800,color:tPrimary}}>
                        {ini}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:sel?700:500,color:"#111",
                          whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                          {u.NAME}
                        </div>
                        {u.USER_GROUPNAME&&(
                          <div style={{fontSize:11,color:"#9CA3AF"}}>{u.USER_GROUPNAME}</div>
                        )}
                      </div>
                      {/* checkbox */}
                      <div style={{width:18,height:18,borderRadius:5,flexShrink:0,
                        border:`2px solid ${sel?tPrimary:"#D1D5DB"}`,
                        background:sel?tPrimary:"#fff",
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {sel && <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8"
                            strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>}
                      </div>
                    </div>
                  );
                })}
                {!loading&&filtered.length===0&&(
                  <div style={{textAlign:"center",padding:"30px",color:"#9CA3AF",fontSize:13}}>
                    No users found
                  </div>
                )}
              </div>
            </>
          )}

          {/* email tab */}
          {tab==="email" && (
            <div style={{padding:"14px 16px",flex:1}}>
              <div style={{border:"1px solid #E5E7EB",borderRadius:8,padding:"6px 8px",
                minHeight:48,display:"flex",flexWrap:"wrap",gap:5,alignItems:"center",
                background:"#fff",cursor:"text"}}
                onClick={()=>document.getElementById("nlEmailInput")?.focus()}>
                {emails.map(e=>(
                  <span key={e} style={{display:"inline-flex",alignItems:"center",gap:4,
                    background:`${tPrimary}15`,color:tPrimary,borderRadius:20,
                    padding:"2px 8px",fontSize:12,fontWeight:600}}>
                    {e}
                    <span onClick={()=>setEmails(p=>p.filter(x=>x!==e))}
                      style={{cursor:"pointer",fontSize:14,lineHeight:1,opacity:0.7}}>×</span>
                  </span>
                ))}
                <input id="nlEmailInput" value={emailInput}
                  onChange={e=>setEmailInput(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"||e.key===","){e.preventDefault();addEmail(emailInput);}}}
                  onBlur={()=>emailInput.trim()&&addEmail(emailInput)}
                  placeholder={emails.length?"":"Enter emails, comma or Enter to add…"}
                  style={{border:"none",outline:"none",fontSize:13,flex:1,minWidth:120,
                    background:"transparent",fontFamily:"inherit",color:"#374151"}}/>
              </div>
              <div style={{fontSize:11,color:"#9CA3AF",marginTop:5}}>
                Separate multiple emails with comma or press Enter
              </div>
            </div>
          )}
        </div>

        {/* result */}
        {result && (
          <div style={{margin:"0 16px 8px",padding:"8px 12px",borderRadius:7,fontSize:12,
            background:result.ok?"#F0FDF4":"#FEF2F2",
            color:result.ok?"#16A34A":"#DC2626",
            border:`1px solid ${result.ok?"#BBF7D0":"#FECACA"}`}}>
            {result.msg}
          </div>
        )}

        {/* footer */}
        <div style={{padding:"12px 16px",borderTop:"1px solid #E5E7EB",flexShrink:0,
          display:"flex",gap:8,justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12,color:"#9CA3AF"}}>
            {totalSelected>0?`${totalSelected} recipient${totalSelected!==1?"s":""} selected`:""}
          </span>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose}
              style={{padding:"7px 16px",borderRadius:7,border:"1px solid #E5E7EB",
                background:"#F7F9FC",color:"#6B7280",fontSize:13,cursor:"pointer",fontWeight:500}}>
              Cancel
            </button>
            <button onClick={send} disabled={!totalSelected||sending}
              style={{padding:"7px 20px",borderRadius:7,border:"none",
                background:!totalSelected||sending?"#aaa":tPrimary,
                color:"#fff",fontSize:13,fontWeight:600,
                cursor:sending?"wait":"pointer"}}>
              {sending?"Sending…":"Share"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
function DetailView({ listDoc, detail, loading, tPrimary, openFile, openImage, subId, user, activeSub }) {
  const [shareOpen, setShareOpen] = useState(false);

  if (!listDoc) return (
    <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",
      flexDirection:"column",gap:"12px",background:"#F8FAFC" }}>
      <Icon name="newsletter" size={52} color="#E5E7EB"/>
      <div style={{ fontSize:"14px",color:"#9CA3AF",fontWeight:"500" }}>
        Select an edition to read
      </div>
    </div>
  );

  const doc     = detail || listDoc;
  const rels    = Array.isArray(doc.RELATIVE)?doc.RELATIVE:[];
  const chatbot = Array.isArray(doc.CHATBOT)?doc.CHATBOT:[];
  const images  = chatbot.filter(a=>isImage(a.FILEPATH));
  const files   = chatbot.filter(a=>!isImage(a.FILEPATH));
  const notes   = doc.NOTES||"";
  const range   = fmtRange(listDoc.DISPLAY_TIME);

  return (
    <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden" }}>

      {/* ── Hero ── */}
      <div style={{ flexShrink:0,
        background:`linear-gradient(135deg,${tPrimary}F0 0%,${tPrimary}80 100%)`,
        padding:"22px 28px",position:"relative",overflow:"hidden" }}>
        {/* Decorative circles */}
        <div style={{ position:"absolute",right:"-30px",top:"-30px",
          width:"150px",height:"150px",borderRadius:"50%",
          background:"rgba(255,255,255,0.06)" }}/>
        <div style={{ position:"absolute",right:"40px",bottom:"-20px",
          width:"80px",height:"80px",borderRadius:"50%",
          background:"rgba(255,255,255,0.08)" }}/>

        <div style={{ position:"relative" }}>
          {/* Logo + Edition label */}
          <div style={{ display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px" }}>
            {doc.LOGO && (
              <img src={doc.LOGO} alt="" onError={e=>e.target.style.display="none"}
                style={{ height:"30px",objectFit:"contain",maxWidth:"90px",
                  background:"rgba(255,255,255,0.15)",borderRadius:"6px",padding:"2px 8px" }}/>
            )}
            <div style={{ display:"inline-flex",alignItems:"center",gap:"5px",
              background:"rgba(255,255,255,0.2)",borderRadius:"20px",
              padding:"2px 10px" }}>
              <Icon name="newsletter" size={10} color="rgba(255,255,255,0.9)"/>
              <span style={{ fontSize:"10px",fontWeight:"700",color:"rgba(255,255,255,0.9)",
                textTransform:"uppercase",letterSpacing:"0.8px" }}>NewsFlash Edition</span>
            </div>
          </div>

          <div style={{ fontSize:"20px",fontWeight:"900",color:"#fff",
            lineHeight:"1.2",marginBottom:"8px",letterSpacing:"-0.3px" }}>
            {listDoc.TITLE||"Untitled"}
          </div>

          <div style={{ display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap" }}>
            {range && <span style={{ fontSize:"11px",color:"rgba(255,255,255,0.8)",
              display:"flex",alignItems:"center",gap:"4px" }}>
              <span>📅</span> {range}
            </span>}

            {files.length>0 && <span style={{ fontSize:"10px",fontWeight:"700",
              color:"#fff",background:"rgba(255,255,255,0.15)",
              borderRadius:"20px",padding:"2px 10px" }}>
              📎 {files.length} attachment{files.length!==1?"s":""}
            </span>}

            <div style={{ flex:1 }}/>
            {/* share button */}
            <button onClick={()=>setShareOpen(true)}
              style={{ display:"flex",alignItems:"center",gap:5,padding:"4px 12px",
                borderRadius:20,border:"1px solid rgba(255,255,255,0.4)",
                background:"rgba(255,255,255,0.15)",color:"#fff",
                fontSize:11,fontWeight:600,cursor:"pointer",transition:"all .12s" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.25)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.15)"}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              Share
            </button>
          </div>
        </div>
      </div>

      {shareOpen && (
        <NewsletterShareModal doc={listDoc} user={user} activeSub={activeSub}
          tPrimary={tPrimary} onClose={()=>setShareOpen(false)}/>
      )}

      {/* ── Body ── */}
      <div style={{ flex:1,overflowY:"auto",background:"#F8FAFC",padding:"20px 24px" }}>

        {loading && <Spin color={tPrimary}/>}

        {!loading && <>

          {/* Attachments */}
          {files.length>0 && (
            <div style={{ display:"flex",gap:"6px",flexWrap:"wrap",marginBottom:"16px" }}>
              {files.map((a,i)=>{ const handleClick=()=>openFile(a.FILEPATH);
                const ext = a.FILEPATH.split(".").pop().toUpperCase();
                const ec  = ext==="PDF"?"#DC2626":ext==="PPTX"?"#D97706":ext==="DOCX"?"#2563EB":"#6B7280";
                const em  = ext==="PDF"?"📄":ext==="PPTX"?"📊":ext==="DOCX"?"📝":"📎";
                const name= a.FILEPATH.replace(/_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}\./,".").split("/").pop();
                return (
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:"5px",
                    padding:"5px 10px",borderRadius:"7px",background:"#fff",
                    border:`1px solid ${ec}33`,fontSize:"11px",color:"#374151" }}>
                    <span>{em}</span>
                    <span style={{ maxWidth:"180px",overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:"500" }}>{name}</span>
                    <span style={{ background:ec,color:"#fff",borderRadius:"4px",
                      padding:"1px 4px",fontSize:"8px",fontWeight:"800" }}>{ext}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Notes */}
          {notes ? (
            <div style={{ background:"#fff",borderRadius:"12px",
              border:"1px solid #E5E7EB",padding:"20px 22px",marginBottom:"20px",
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
                textTransform:"uppercase",letterSpacing:"0.7px",marginBottom:"12px" }}>
                📋 Summary
              </div>
              <div style={{ fontSize:"13px",color:"#374151",lineHeight:"1.75" }}
                dangerouslySetInnerHTML={{ __html:notes }}/>
            </div>
          ) : null}

          {/* Related documents — list view */}
          {rels.length>0 && (
            <div style={{ display:"flex",flexDirection:"column",gap:"6px",marginTop:"4px" }}>
              <div style={{ fontSize:"10px",fontWeight:"700",color:"#9CA3AF",
                textTransform:"uppercase",letterSpacing:"0.7px",marginBottom:"4px" }}>
                🔗 Related ({rels.length})
              </div>
              {rels.map(r=><RelatedCard key={r.DOCUMENTID} doc={r} tPrimary={tPrimary} subId={subId}/>)}
            </div>
          )}
        </>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function NewsFlashScreen({ user, subproject }) {
  const [selected,   setSelected]   = useState(null);
  const [search,     setSearch]     = useState("");
  const [activeOnly, setActiveOnly] = useState(true); // true=stateid 2, false=stateid 1
  const ctx      = useContext(ThemeCtx)||{};
  const theme    = ctx.theme||ctx||{};
  const tPrimary = theme.primary||C.primary;
  const isEditor = subproject?.ROLE === 1; // only editors see the funnel

  const { items, loading:listLoading } = useListLoader({ user, subproject,
    stateid: isEditor ? (activeOnly ? 2 : 1) : 2 });
  const { lightbox, openFile, openImage } = useFileViewer();
  const { detail, loading:detailLoading } = useDetailLoader({
    user, subproject, documentId: selected?.DOCUMENTID
  });

  // Auto-select first on load
  // Reset selection when subproject switches, then auto-select first item
  useEffect(()=>{ setSelected(null); }, [subproject?.SUBPROJECTID]);
  useEffect(()=>{ if(items.length>0) setSelected(items[0]); }, [items]);

  // Group by year
  const years = [...new Set(items.map(d=>yearOf(d.STARTDATE)))].sort((a,b)=>b-a);
  const filtered = search
    ? items.filter(d=>(d.TITLE||"").toLowerCase().includes(search.toLowerCase()))
    : items;
  const grouped = years.reduce((acc,y)=>({
    ...acc,
    [y]: filtered.filter(d=>yearOf(d.STARTDATE)===y)
  }),{});

  return (
    <div style={{ display:"flex",height:"100%",overflow:"hidden",background:"#F0F4F8" }}>

      {/* ══ LEFT PANEL ══════════════════════════════════════════════════════ */}
      <div style={{ width:"280px",flexShrink:0,display:"flex",flexDirection:"column",
        background:"#fff",borderRight:"1px solid #E4EAF2",overflow:"hidden" }}>

        {/* Header */}
        <div style={{ flexShrink:0,
          background:`linear-gradient(135deg,${tPrimary}EE,${tPrimary}88)`,
          padding:"14px 16px" }}>
          <div style={{ display:"flex",alignItems:"center",gap:"8px",marginBottom:"10px" }}>
            <Icon name="newsletter" size={15} color="rgba(255,255,255,0.9)"/>
            <span style={{ fontSize:"14px",fontWeight:"800",color:"#fff",flex:1 }}>NewsFlash</span>
            <span style={{ fontSize:"11px",fontWeight:"700",
              color:"rgba(255,255,255,0.85)",background:"rgba(255,255,255,0.2)",
              borderRadius:"10px",padding:"1px 8px" }}>{items.length}</span>
            {/* Funnel — editor only */}
            {isEditor && (
              <button onClick={()=>{ setActiveOnly(a=>!a); setSelected(null); }}
                title={activeOnly?"Showing Active — click to show Inactive":"Showing Inactive — click to show Active"}
                style={{ width:"28px",height:"28px",borderRadius:"7px",border:"none",
                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                  background: activeOnly?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.2)",
                  transition:"all 0.15s" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={activeOnly?tPrimary:"rgba(255,255,255,0.85)"} strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </button>
            )}
          </div>
          {/* Active/Inactive label — editor only */}
          {isEditor && (
            <div style={{ fontSize:"10px",fontWeight:"600",marginBottom:"8px",
              color:"rgba(255,255,255,0.75)",display:"flex",alignItems:"center",gap:"5px" }}>
              <div style={{ width:"6px",height:"6px",borderRadius:"50%",
                background: activeOnly?"#4ADE80":"#F87171" }}/>
              {activeOnly ? "Active editions" : "Inactive editions"}
            </div>
          )}
          {/* Search */}
          <div style={{ display:"flex",alignItems:"center",gap:"6px",
            background:"rgba(255,255,255,0.18)",borderRadius:"8px",
            padding:"5px 10px",border:"1px solid rgba(255,255,255,0.2)" }}>
            <Icon name="search" size={12} color="rgba(255,255,255,0.7)"/>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search editions..."
              style={{ border:"none",background:"none",outline:"none",
                fontSize:"12px",color:"#fff",width:"100%",
                caretColor:"#fff" }}/>
            {search&&<button onClick={()=>setSearch("")}
              style={{ border:"none",background:"none",cursor:"pointer",
                color:"rgba(255,255,255,0.7)",fontSize:"15px",padding:0,lineHeight:1 }}>×</button>}
          </div>
        </div>

        {/* List grouped by year */}
        <div style={{ flex:1,overflowY:"auto" }}>
          {listLoading && <Spin color={tPrimary}/>}
          {!listLoading && filtered.length===0 && (
            <div style={{ padding:"2rem",textAlign:"center",color:"#9CA3AF",fontSize:"13px" }}>
              No editions found
            </div>
          )}
          {!listLoading && years.map(yr=>{
            const group = grouped[yr]||[];
            if(!group.length) return null;
            return (
              <div key={yr}>
                {/* Year divider */}
                <div style={{ padding:"6px 14px 4px",
                  fontSize:"10px",fontWeight:"800",color:"#9CA3AF",
                  textTransform:"uppercase",letterSpacing:"1px",
                  background:"#F8FAFC",borderBottom:"1px solid #F1F5F9",
                  borderTop:"1px solid #F1F5F9",
                  position:"sticky",top:0,zIndex:1 }}>
                  {yr}
                </div>
                {group.map(doc=>(
                  <ListItem key={doc.DOCUMENTID} doc={doc}
                    active={selected?.DOCUMENTID===doc.DOCUMENTID}
                    onClick={()=>setSelected(doc)}
                    tPrimary={tPrimary}/>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* ══ RIGHT PANEL ═════════════════════════════════════════════════════ */}
      <div style={{ flex:1,display:"flex",overflow:"hidden",minWidth:0 }}>
        <DetailView
          listDoc={selected}
          detail={detail}
          loading={detailLoading}
          tPrimary={tPrimary}
          openFile={openFile}
          openImage={openImage}
          subId={subproject?.SUBPROJECTID}
          user={user}
          activeSub={subproject}/>
      </div>
    {lightbox}
    </div>
  );
}